import { useMsal } from "@azure/msal-react";
import { useAccount } from "@racwa/react-adb2c";
import { ClaimsEftApiClient } from "raci-claimseft-clientproxy";
import { LookupServiceApiClient } from "raci-lookup-service-clientproxy";
import {
  DEFAULT_SHIELD_ENVIRONMENT,
  HTTP_HEADER_KEY_RACWA_NPE_AUTHORIZATION,
  HTTP_HEADER_KEY_SHIELD_ENVIRONMENT,
  useFeatureToggles,
  useShieldEnvironment,
} from "raci-react-library-npe";
import { useMemo } from "react";
import { UseApiClientProps } from "../../shared/hooks/useApiClient";
import { SESSION_ID_KEY } from "../../shared/hooks/useCreateSession";

const useApi = <T>({ Client, apiBaseUrl }: UseApiClientProps<T>) => {
  const { instance } = useMsal();
  const account = useAccount({
    clientId: process.env.REACT_APP_OAUTH_CLIENT_ID ?? undefined,
  });
  const { selected: selectedShieldEnv } = useShieldEnvironment();
  const [toggles] = useFeatureToggles();

  return useMemo(() => {
    const npeLoginRequest = {
      scopes: [],
      redirectUri: "/",
      account: account,
    };

    const fetchWrapper = async (url: RequestInfo, init?: RequestInit): Promise<Response> => {
      const sessionId = sessionStorage.getItem(SESSION_ID_KEY);
      const headers: any = { ...init?.headers };

      let token = "";

      try {
        const tokenResponse = await instance.acquireTokenSilent(npeLoginRequest);
        token = tokenResponse.idToken;
      } catch (ex) {
        await instance.acquireTokenRedirect(npeLoginRequest);
      }

      if (sessionId) {
        headers.SessionId = sessionId;
      }

      toggles?.forEach((toggle) => {
        if (toggle.enabled !== null) {
          headers[toggle.headerName] = toggle.enabled;
        }
      });

      if (selectedShieldEnv && selectedShieldEnv !== DEFAULT_SHIELD_ENVIRONMENT) {
        headers[HTTP_HEADER_KEY_SHIELD_ENVIRONMENT] = selectedShieldEnv;
      }

      if (token) {
        headers[HTTP_HEADER_KEY_RACWA_NPE_AUTHORIZATION] = token;
      }

      const response = await fetch(url, {
        ...init,
        headers,
      });
      return response;
    };

    return new Client(apiBaseUrl, { fetch: fetchWrapper });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShieldEnv, toggles]);
};

const useApiClient = () => {
  return useApi({ Client: ClaimsEftApiClient, apiBaseUrl: process.env.REACT_APP_API_BASE_URL || "" });
};

const useLookupApiClient = () => {
  return useApi({ Client: LookupServiceApiClient, apiBaseUrl: process.env.REACT_APP_LOOKUP_SERVICE_API_URL || "" });
};

export { useApiClient, useLookupApiClient };
export default useApiClient;
